import { Slot, Slottable } from "@radix-ui/react-slot";
import { cn } from "@sylviq/ui-react-web/lib/cn";
import type { ButtonHTMLAttributes, ReactNode } from "react";

export interface ProductLockupProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /**
   * Render as the caller's own element (a router link) rather than a
   * `<button>`. Pass it childless — the mark and wordmark compose into it.
   */
  asChild?: boolean;
  /**
   * The product's mark: an `<img>`, an inline `<svg>`, or a `.product-mark`
   * initials span. Supplied by the product — this package ships no brand.
   */
  mark?: ReactNode;
  /** The product's name. Hidden in the collapsed rail. */
  wordmark: ReactNode;
  children?: ReactNode;
}

/**
 * Brand lockup for the sidebar header: mark plus wordmark, optionally the
 * home affordance. Deliberately content-free — every product passes its own
 * mark and name, and nothing about either is styled here beyond layout.
 */
export function ProductLockup({
  asChild,
  mark,
  wordmark,
  className,
  children,
  ...props
}: ProductLockupProps) {
  const interactive = asChild || props.onClick !== undefined;
  const Component = asChild ? Slot : interactive ? "button" : "span";
  return (
    <Component
      type={Component === "button" ? "button" : undefined}
      className={cn("product-lockup", className)}
      {...props}
    >
      {mark}
      <span>{wordmark}</span>
      {asChild ? <Slottable>{children}</Slottable> : null}
    </Component>
  );
}
